import { useState, useEffect, useCallback } from 'react';
import { confetti } from '@tsparticles/confetti';
import { useTranslation } from 'react-i18next';
import SEO from './SEO';
import TikTokTooltip from './TikTokTooltip';
import './HomePage.css';
import './utils/buttons.css';

const CARD_EXIT_MS = 260;
const HOME_THEME_COLOR = '#c119fa';
const HOME_THEME_COLOR_PENALTY = '#ff2f53';

function Hero() {
  const { t, i18n } = useTranslation();
  const [showTikTokTooltip, setShowTikTokTooltip] = useState(false);
  const [dareIndex, setDareIndex] = useState(0);
  const [cardView, setCardView] = useState('dare');
  const [isCardTransitioning, setIsCardTransitioning] = useState(false);

  const transitionCard = useCallback((nextView, shouldAdvanceDare = false) => {
    if (isCardTransitioning) {
      return;
    }

    const translatedDares = t('home.dares', { returnObjects: true });
    const dares = Array.isArray(translatedDares) ? translatedDares : [];

    setIsCardTransitioning(true);

    window.setTimeout(() => {
      if (shouldAdvanceDare) {
        setDareIndex((currentIndex) => (currentIndex + 1) % dares.length);
      }

      setCardView(nextView);
      setIsCardTransitioning(false);
    }, CARD_EXIT_MS);
  }, [isCardTransitioning, t]);

  const showNextDare = useCallback(() => {
    transitionCard('dare', true);
  }, [transitionCard]);

  const showPenalty = useCallback(() => {
    transitionCard('penalty');
  }, [transitionCard]);

  const throwDoneConfetti = useCallback(() => {
    confetti({
      count: 90,
      spread: 80,
      startVelocity: 45,
      decay: 0.9,
      gravity: 1,
      ticks: 180,
      position: {
        x: 50,
        y: 58,
      },
      colors: ['#42ffb7', '#42e0ff', '#ff4274', '#ffffff'],
      shapes: ['square', 'circle'],
      scalar: 0.9,
      zIndex: 9999,
      disableForReducedMotion: true,
    });
  }, []);

  const handleDone = useCallback(() => {
    throwDoneConfetti();
    showNextDare();
  }, [showNextDare, throwDoneConfetti]);
  
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isTikTok = userAgent.toLowerCase().includes('tiktok') || 
                     userAgent.toLowerCase().includes('musical_ly') ||
                     userAgent.toLowerCase().includes('bytedance');
    
    if (isTikTok && !sessionStorage.getItem('tiktokTooltipShown')) {
      setShowTikTokTooltip(true);
      sessionStorage.setItem('tiktokTooltipShown', 'true');
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && cardView === 'dare') {
        showNextDare();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [cardView, showNextDare]);

  const translatedDares = t('home.dares', { returnObjects: true });
  const dares = Array.isArray(translatedDares) ? translatedDares : [];
  const currentDare = dares[dareIndex] || dares[0];
  const isPenalty = cardView === 'penalty';
  const titleDare = t('home.titleDare');
  const titleAnyone = t('home.titleAnyone');
  const storeLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const androidStoreUrl = `https://play.google.com/store/apps/details?id=app.taptrap&hl=${storeLanguage}`;

  useEffect(() => {
    const html = document.documentElement;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const previousHtmlBackground = html.style.backgroundColor;
    const previousThemeColor = themeColorMeta?.getAttribute('content');
    const nextThemeColor = isPenalty ? HOME_THEME_COLOR_PENALTY : HOME_THEME_COLOR;

    html.style.backgroundColor = nextThemeColor;

    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', nextThemeColor);
    }

    return () => {
      html.style.backgroundColor = previousHtmlBackground;

      if (themeColorMeta && previousThemeColor) {
        themeColorMeta.setAttribute('content', previousThemeColor);
      }
    };
  }, [isPenalty]);
  
  return (
    <>
      <SEO page="home" themeColor={isPenalty ? HOME_THEME_COLOR_PENALTY : HOME_THEME_COLOR} />
      {showTikTokTooltip && (
        <TikTokTooltip onClose={() => setShowTikTokTooltip(false)} />
      )}
      <section className={`home-hero${isPenalty ? ' home-hero-penalty' : ''}`} aria-labelledby="home-hero-title">
        <div className="home-hero-copy">
          <h1 className="home-title-art" id="home-hero-title" aria-label={t('home.titleAria')}>
            <span data-text={titleDare}>{titleDare}</span>
            <span data-text={titleAnyone}>{titleAnyone}</span>
          </h1>
          <p className="home-hero-subtitle">
            {t('home.subtitle')}
          </p>
        </div>

        <div className="home-phone-art" aria-label={t('home.phonePreviewAria')}>
          <div className="home-phone-shell" aria-hidden="true">
            <div className="phone-status">
              <span>9:41</span>
              <span className="phone-status-icons">
                <img src="/assets/home/cellular.svg" alt="" className="phone-cellular" />
                <img src="/assets/home/wifi.svg" alt="" className="phone-wifi" />
                <img src="/assets/home/battery.svg" alt="" className="phone-battery" />
              </span>
            </div>
            <div className="phone-nav-icons">
              <img src="/assets/home/home.svg" alt="" className="phone-home-icon" />
              <img src="/assets/home/settings.svg" alt="" className="phone-gear-icon" />
            </div>
            <span className="phone-home-indicator"></span>
          </div>

          <img src="/assets/home/cool-face.svg" alt="" className="cool-face" aria-hidden="true" />

          <div className={`dare-card${isPenalty ? ' penalty-card' : ''}${isCardTransitioning ? ' is-exiting' : ' is-entering'}`}>
            {isPenalty ? (
              <div className="penalty-card-content">
                <img src="/assets/home/penalty-icon.svg" alt="" className="penalty-icon" aria-hidden="true" />
                <p className="penalty-kicker">{t('home.penalty')}</p>
                <p className="penalty-text">{t('home.penaltyText')}</p>
                <button type="button" className="penalty-next-button" onClick={showNextDare}>
                  <img src="/assets/home/penalty-highlight.svg" alt="" className="penalty-button-highlight" aria-hidden="true" />
                  <span>{t('home.nextMatch')}</span>
                  <img src="/assets/home/penalty-arrow.svg" alt="" className="penalty-button-arrow" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <>
                <div className="dare-card-pattern">
                  <p className="dare-card-kicker">{t('home.dareLabel')}</p>
                </div>
                <div className="dare-card-content">
                  <p className="dare-card-text">
                    {currentDare.map((line, index) => (
                      <span key={`${line}-${index}`}>
                        {line}
                        {index < currentDare.length - 1 && <br />}
                      </span>
                    ))}
                  </p>
                </div>
                <div className="dare-card-actions">
                  <button type="button" className="action-button action-button-skip" onClick={showNextDare} aria-label={t('home.skipAria')}>
                    <span className="action-icon-wrap">
                      <img src="/assets/home/arrow.svg" alt="" className="action-arrow" />
                    </span>
                    <span className="action-label">{t('home.skip')}</span>
                  </button>
                  <button type="button" className="action-button action-button-refuse" onClick={showPenalty} aria-label={t('home.refuseAria')}>
                    <span className="action-icon-wrap">
                      <img src="/assets/home/refuse.svg" alt="" className="action-x" />
                    </span>
                    <span className="action-label">{t('home.refuse')}</span>
                  </button>
                  <button type="button" className="action-button action-button-done" onClick={handleDone} aria-label={t('home.doneAria')}>
                    <span className="action-icon-wrap">
                      <img src="/assets/home/done.svg" alt="" className="action-check" />
                    </span>
                    <span className="action-label">{t('home.done')}</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <img src="/assets/home/eyes.svg" alt="" className="eye-mascot" aria-hidden="true" />
        </div>

        <div className="download-buttons home-download-buttons" id="get-it">
          <a href="https://apps.apple.com/app/taptrap-party-game/id6742859366" className="btn primary home-store-button">
            <img src="/assets/icon-apple.svg" alt="" className="btn-icon" />
            <span>{t('home.getIOS')}</span>
          </a>
          <a href={androidStoreUrl} className="btn secondary home-store-button">
            <img src="/assets/icon-android.svg" alt="" className="btn-icon" />
            <span>{t('home.getAndroid')}</span>
          </a>
        </div>
      </section>
    </>
  );
}

export default Hero;
